import { EventEmitter } from 'events';
import { http, createPublicClient } from 'viem';
import { mainnet } from 'viem/chains';
import * as sdkErrors from './constants/errors.js';
import { PYTH_SERVER_DEFAULT } from './constants/prices.js';
import { allContractsByNetwork } from './contracts/index.js';
import { PerpsProvider, SupportedNetworkIds, } from './types/index.js';
import { createClients, createEIP7412Clients, createWebSocketClients } from './utils/index.js';
const DEFAULT_CHAIN = SupportedNetworkIds.BASE_MAINNET;
const DEFAULT_CONTEXT = {
    supportedChains: {
        [DEFAULT_CHAIN]: [],
    },
    apiUrl: 'https://api.kwenta.io',
    proxyUrl: 'https://api.kwenta.io/proxy',
    pythUrls: {
        primary: PYTH_SERVER_DEFAULT,
    },
    logError: () => console.error,
};
export default class Context {
    context;
    contractConfigs;
    events = new EventEmitter().setMaxListeners(100);
    l1MainnetProvider;
    clients;
    websocketClients;
    eip7412clients;
    constructor(context) {
        this.context = { ...DEFAULT_CONTEXT, ...context };
        if (context.walletClient) {
            this.setWalletClient(context.walletClient);
        }
        this.clients = createClients(this.context.supportedChains);
        this.websocketClients = createWebSocketClients(this.context.supportedChains);
        this.eip7412clients = createEIP7412Clients(this.context.supportedChains);
        this.contractConfigs = Object.entries(this.context.supportedChains).reduce((acc, [chain, _]) => {
            const networkId = Number(chain);
            acc[networkId] = allContractsByNetwork(networkId);
            return acc;
        }, {});
        this.l1MainnetProvider = createPublicClient({
            chain: mainnet,
            transport: http(),
        });
    }
    get supportedChains() {
        return this.context.supportedChains;
    }
    get signerChainId() {
        if (!this.context.walletClient) {
            throw new Error(sdkErrors.NO_SIGNER);
        }
        return this.context.walletClient.chain.id;
    }
    get signerPublicClient() {
        const client = this.clients[this.signerChainId] ?? this.clients[DEFAULT_CHAIN];
        if (!client)
            throw new Error(`No client found for network ${this.signerChainId}`);
        return client;
    }
    get walletClient() {
        if (!this.context.walletClient) {
            throw new Error(sdkErrors.NO_SIGNER);
        }
        return this.context.walletClient;
    }
    get walletAddress() {
        if (!this.context.walletAddress) {
            throw new Error(sdkErrors.NO_SIGNER);
        }
        return this.context.walletAddress;
    }
    get apiUrl() {
        return this.context.apiUrl;
    }
    get satsumaApiKey() {
        return this.context.satsumaApiKey;
    }
    get pythUrl() {
        return this.context.pythUrls?.primary || PYTH_SERVER_DEFAULT;
    }
    get pythBackupUrl() {
        return this.context.pythUrls?.secondary;
    }
    get pythUrls() {
        return this.context.pythUrls || { primary: PYTH_SERVER_DEFAULT };
    }
    get proxyUrl() {
        return this.context.proxyUrl;
    }
    get subgraphUrls() {
        const { satsumaApiKey } = this.context;
        return {
            [PerpsProvider.SNX_V2_OP]: {
                10: `https://subgraph.satsuma-prod.com/${satsumaApiKey}/kwenta/optimism-perps/api`,
                11155420: `https://subgraph.satsuma-prod.com/${satsumaApiKey}/kwenta/optimism-sepolia-perps/api`,
            },
            [PerpsProvider.SNX_V3_BASE]: {
                84532: `https://subgraph.satsuma-prod.com/${satsumaApiKey}/kwenta/base-sepolia-perps-v3/api`,
                8453: `https://subgraph.satsuma-prod.com/${satsumaApiKey}/kwenta/base-perps-v3/api`,
            },
        };
    }
    publicClient(chainId) {
        const client = this.clients[chainId];
        if (client)
            return client;
        throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
    }
    async setWalletClient(client) {
        // TODO: Handle multiple addresses
        this.context.walletAddress = (await client.getAddresses())[0];
        this.context.walletClient = client;
    }
    setWalletAddress(walletAddress) {
        this.context.walletAddress = walletAddress;
    }
    logError(err, skipReport = false) {
        return this.context.logError?.(err, skipReport);
    }
}
//# sourceMappingURL=context.js.map